// Phase 9.1.1 — Applicability picker (reusable card + modal body).
//
// Given an initial list of `applicable_location_ids` (or null for
// "everywhere") and the full active-locations list, renders a
// checkbox tree with a top-level "Applicable everywhere" master
// toggle. On Save, calls the parent's onSave with the new list
// (null when the master is on, else the array of picked ids).

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";

export function ApplicabilityPicker({
  initialApplicable,          // null (everywhere) OR list of ids
  activeLocations,            // list of { location_id, display_name, is_warehouse }
  onSave,                     // async (ids: string[] | null) => void
  onCancel,
  saving,
  testidPrefix = "applicability",
}) {
  const [everywhere, setEverywhere] = useState(initialApplicable == null);
  const [picked, setPicked] = useState(
    new Set(initialApplicable || activeLocations.map((l) => l.location_id)),
  );

  useEffect(() => {
    setEverywhere(initialApplicable == null);
    setPicked(new Set(initialApplicable || activeLocations.map((l) => l.location_id)));
  }, [initialApplicable, activeLocations]);

  const toggle = (loc_id) => {
    if (everywhere) return;
    const next = new Set(picked);
    if (next.has(loc_id)) next.delete(loc_id);
    else next.add(loc_id);
    setPicked(next);
  };

  const selectAll = () => setPicked(new Set(activeLocations.map((l) => l.location_id)));
  const selectNone = () => setPicked(new Set());

  const handleSave = () => {
    if (everywhere) {
      onSave(null);
    } else {
      onSave(Array.from(picked));
    }
  };

  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-picker`}>
      <label className="flex items-center gap-2 cursor-pointer p-2 rounded-md hover:bg-muted/40">
        <Checkbox
          checked={everywhere}
          onCheckedChange={(v) => setEverywhere(!!v)}
          data-testid={`${testidPrefix}-everywhere`}
        />
        <div className="text-sm font-medium">Applicable at all active locations</div>
      </label>
      <div className={everywhere ? "opacity-40 pointer-events-none" : ""}>
        <div className="flex items-center justify-between px-2">
          <div className="text-xs text-muted-foreground">
            {picked.size} of {activeLocations.length} locations selected
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={selectAll} className="h-6 text-xs" data-testid={`${testidPrefix}-select-all`}>
              All
            </Button>
            <Button size="sm" variant="ghost" onClick={selectNone} className="h-6 text-xs" data-testid={`${testidPrefix}-select-none`}>
              None
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1 max-h-72 overflow-y-auto border border-border/60 rounded-md p-2">
          {activeLocations.map((l) => (
            <label
              key={l.location_id}
              className="flex items-center gap-2 cursor-pointer p-1.5 rounded-sm hover:bg-muted/40"
            >
              <Checkbox
                checked={picked.has(l.location_id)}
                onCheckedChange={() => toggle(l.location_id)}
                data-testid={`${testidPrefix}-checkbox-${l.location_id}`}
              />
              <div className="min-w-0 flex-1">
                <div className="text-xs truncate">{l.display_name || l.location_id}</div>
                <div className="text-[10px] text-muted-foreground font-mono truncate">
                  {l.location_id}
                </div>
              </div>
              {l.is_warehouse && (
                <Badge variant="outline" className="text-[9px]">warehouse</Badge>
              )}
            </label>
          ))}
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground pt-1 border-t border-border/40">
        Uncheck locations where this item is not sold or stocked. Non-applicable locations
        appear as &quot;N/A&quot; instead of &quot;Out of stock&quot; on the reorder dashboard.
      </p>
      <div className="flex justify-end gap-2 pt-2">
        {onCancel && (
          <Button
            variant="ghost" size="sm" onClick={onCancel} disabled={saving}
            data-testid={`${testidPrefix}-cancel`}
          >
            Cancel
          </Button>
        )}
        <Button
          size="sm"
          onClick={handleSave}
          disabled={saving}
          data-testid={`${testidPrefix}-save`}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}
